import { useState } from "react";
import { ManualLink } from "@/components/ManualLink";
import { Check, Copy, Cpu, KeyRound, Package, Receipt, ShieldCheck, Sparkles, Terminal, Wallet } from "lucide-react";
import { BinaryGrid, useBins } from "@/components/BinaryGrid";
import { FeatureDemos } from "@/components/FeatureDemos";
import { GenAlphaGuide } from "@/components/GenAlphaGuide";
import { RecastSection, MINT } from "@/components/RecastSection";
import { AiPricing } from "@/components/PricingSection";
import { SupportSection } from "@/components/SupportSection";
import { BuyForTeam, QuoteCard } from "@/components/QuoteCard";

const AMBER = "#e69d27";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


const OS_PATHS = {
  mac:   { label: "macOS",   path: "/Library/OFX/Plugins/",            quar: "sudo xattr -dr com.apple.quarantine /Library/OFX/Plugins/KolorKlinicGenAlpha.ofx.bundle /Library/OFX/Plugins/KolorKlinicRecast.ofx.bundle" },
  linux: { label: "Linux",   path: "/usr/OFX/Plugins/",                 quar: null },
  win:   { label: "Windows", path: "C:\\Program Files\\Common Files\\OFX\\Plugins\\", quar: null },
};

function CopyBtn({ value, testid, label = "copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="btn-ghost px-2 py-1 flex items-center gap-1 text-xs"
      data-testid={testid}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        } catch { /* noop */ }
      }}
    >
      {done ? <Check size={12} /> : <Copy size={12} />}
      {done ? "copied" : label}
    </button>
  );
}

const AiHero = () => (
  <section className="relative film-grain overflow-hidden hair-border bg-[#101214]" data-testid="ai-hero">
    <div className="absolute -right-24 -top-24 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl" style={{ background: MINT }} aria-hidden />
    <div className="absolute right-40 bottom-[-180px] w-[380px] h-[380px] rounded-full opacity-15 blur-3xl" style={{ background: AMBER }} aria-hidden />
    <div className="relative px-10 py-16 max-w-[1100px]">
      <div className="chip mb-6" style={{ color: MINT, borderColor: MINT }}><Sparkles size={12} /> AI tools · native OFX · bring your own fal.ai key</div>
      <h1 className="font-display font-black uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight" data-testid="ai-title">
        KolorKlinic <span style={{ color: MINT }}>A</span><span style={{ color: AMBER }}>I</span>
      </h1>
      <p className="mt-4 font-display italic text-xl md:text-2xl text-[#f2f4f8]">AI where you already work: inside DaVinci Resolve.</p>
      <p className="mt-6 max-w-2xl text-base md:text-lg text-[#9ba3b4] leading-relaxed">
        Pull a clean character matte for a whole shot, or fix a performance without a reshoot. Pay once for the plugin, then run it on your
        own fal.ai account: no subscription, no markup, and the real cost of every job in the plugin panel.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <a href="#gen-alpha" className="px-6 py-3 font-semibold uppercase text-black transition-opacity hover:opacity-85" style={{ background: AMBER }} data-testid="ai-hero-genalpha-btn">Gen Alpha · AI matte</a>
        <a href="#recast" className="px-6 py-3 font-semibold uppercase text-black transition-opacity hover:opacity-85" style={{ background: MINT }} data-testid="ai-hero-recast-btn">Recast · performance fix</a>
        <a href="#fal-key" className="btn-ghost px-6 py-3" data-testid="ai-hero-falkey-btn">How the fal.ai key works</a>
        <a href="#pricing" className="btn-ghost px-6 py-3" data-testid="ai-hero-pricing-btn">Pricing</a>
      </div>
    </div>
  </section>
);

const STEPS = [
  { icon: KeyRound, title: "1 · Get a key", body: <>Create a free account at <a className="underline" style={{ color: MINT }} href="https://fal.ai" target="_blank" rel="noreferrer">fal.ai</a>, add a little credit, and create a key with the FULL preset at <a className="underline" style={{ color: MINT }} href="https://fal.ai/dashboard/keys" target="_blank" rel="noreferrer">fal.ai/dashboard/keys</a>.</> },
  { icon: ShieldCheck, title: "2 · Paste it once", body: "Paste it in the plugin panel. It's saved on your computer only, shared by Gen Alpha and Recast, and you can forget it with one click." },
  { icon: Receipt, title: "3 · Check before you run", body: "\"Check fal.ai key & cost\" tests the key and estimates the job before anything is billed." },
  { icon: Wallet, title: "4 · See the real cost", body: "After each job the panel shows what fal.ai actually charged, with a running total. We never add a markup." },
];

const COSTS = [
  ["Gen Alpha · character matte", "about $0.10 – 0.50 per second of footage", AMBER],
  ["Recast · sliders (smile, gaze, head…)", "about $0.005 per changed frame · a 5 s fix ≈ $0.60", MINT],
  ["Recast · retarget from a driver clip", "about $0.015 per second · a 10 s shot ≈ $0.15", MINT],
  ["Recast · remove blink", "free, runs without fal.ai", MINT],
];

const TOOLS = [
  { id: "matte", label: "Gen Alpha matte", color: AMBER, cost: (sec) => [sec * 0.10, sec * 0.50] },
  { id: "sliders", label: "Recast sliders", color: MINT, cost: (sec, fps, pct) => { const f = Math.ceil(sec * fps * pct / 100); return [f * 0.004, f * 0.006]; } },
  { id: "retarget", label: "Recast retarget", color: MINT, cost: (sec) => [sec * 0.012, sec * 0.018] },
  { id: "blink", label: "Remove blink", color: MINT, cost: () => [0, 0] },
];
const usd = (v) => (v < 0.01 && v > 0 ? "< $0.01" : `$${v.toFixed(2)}`);

const FPS = [23.976, 24, 25, 29.97, 30, 50, 60];
const initial = () => {
  const q = new URLSearchParams(window.location.search);
  const num = (k, d, lo, hi) => { const v = Number(q.get(k)); return q.has(k) && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d; };
  const tool = TOOLS.some((t) => t.id === q.get("tool")) ? q.get("tool") : "sliders";
  const fps = num("fps", 25, 1, 120);
  return { shared: q.has("tool"), tool, sec: num("s", 5, 0, 600), fps: FPS.includes(fps) ? fps : 25, pct: Math.round(num("pct", 100, 5, 100) / 5) * 5 };
};

const CostCalculator = () => {
  const [init] = useState(initial);
  const [tool, setTool] = useState(init.tool);
  const [sec, setSec] = useState(init.sec);
  const [fps, setFps] = useState(init.fps);
  const [pct, setPct] = useState(init.pct);
  const [copied, setCopied] = useState(false);
  const t = TOOLS.find((x) => x.id === tool);
  const s = Math.min(600, Math.max(0, Number(sec) || 0));
  const [lo, hi] = t.cost(s, fps, pct);
  const share = async () => {
    const q = new URLSearchParams({ tool, s: String(s) });
    if (tool === "sliders") { q.set("fps", String(fps)); q.set("pct", String(pct)); }
    const url = `${window.location.origin}/ai?${q.toString()}#fal-key`;
    try { await navigator.clipboard.writeText(url); } catch { window.prompt("Copy this link", url); }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const field = "bg-[#0e1013] border border-[#2a2f3d] px-3 py-2 text-sm text-white focus:border-white outline-none transition-colors";
  const qt = TOOLS.find((x) => x.id === init.tool);
  const [qlo, qhi] = qt.cost(init.sec, init.fps, init.pct);
  return (
    <>
    {init.shared && <QuoteCard tool={init.tool} label={qt.label} sec={init.sec} fps={init.fps} pct={init.pct} showFrames={init.tool === "sliders"}
      cost={init.tool === "blink" ? "Free" : init.sec === 0 ? "-" : `${usd(qlo)} – ${usd(qhi)}`} />}
    <div className="border p-5 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6" style={{ borderColor: MINT }} data-testid="ai-cost-calculator">
      <div className="space-y-4">
        <h3 className="font-display uppercase text-xl">Cost calculator</h3>
        <div className="flex flex-wrap gap-2" data-testid="calc-tool-group">
          {TOOLS.map((x) => (
            <button key={x.id} onClick={() => setTool(x.id)} className="px-3 py-1.5 text-xs font-mono uppercase border transition-colors"
              style={tool === x.id ? { background: x.color, borderColor: x.color, color: "#000" } : { borderColor: "#2a2f3d", color: "#9ba3b4" }} data-testid={`calc-tool-${x.id}`}>{x.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-4 items-end">
          <label className="text-xs text-[#9ba3b4] space-y-1"><span className="block">Shot length (seconds)</span>
            <input type="number" min="0" max="600" step="0.5" value={sec} onChange={(e) => setSec(e.target.value)} onBlur={() => setSec(s)} className={`${field} w-32`} data-testid="calc-seconds-input" /></label>
          {tool !== "blink" && tool !== "matte" && tool !== "retarget" && (
            <label className="text-xs text-[#9ba3b4] space-y-1"><span className="block">Frame rate</span>
              <select value={fps} onChange={(e) => setFps(Number(e.target.value))} className={field} data-testid="calc-fps-select">
                {FPS.map((f) => <option key={f} value={f}>{f} fps</option>)}
              </select></label>
          )}
          {tool === "sliders" && (
            <label className="text-xs text-[#9ba3b4] space-y-1 flex-1 min-w-[180px]"><span className="block">Frames changed: {pct}%</span>
              <input type="range" min="5" max="100" step="5" value={pct} onChange={(e) => setPct(Number(e.target.value))} className="w-full accent-[#4ff2c2]" data-testid="calc-percent-range" /></label>
          )}
        </div>
        <p className="text-xs text-[#8b93a7]">{tool === "sliders" ? "Recast only sends the frames where a slider is moved, so a short keyframed fix costs less than the full shot." : tool === "blink" ? "Blink removal runs on our server without fal.ai." : "Billed by fal.ai to your own account."}</p>
      </div>
      <div className="flex flex-col justify-center bg-[#0e1013] border border-[#2a2f3d] p-5" data-testid="calc-result">
        <span className="text-xs font-mono uppercase text-[#9ba3b4]">Estimated fal.ai cost</span>
        <span className="font-display text-5xl mt-1" style={{ color: t.color }} data-testid="calc-result-value">{tool === "blink" ? "Free" : s === 0 ? "Enter a length" : `${usd(lo)} – ${usd(hi)}`}</span>
        <span className="text-xs text-[#8b93a7] mt-2">for {s}s with {t.label}. Plugin licence is a one-time €99.</span>
        <button onClick={share} className="mt-4 self-start flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase border transition-colors hover:bg-[#4ff2c2] hover:text-black" style={{ borderColor: MINT, color: copied ? "#000" : MINT, background: copied ? MINT : "transparent" }} data-testid="calc-share-btn">
          {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Link copied" : "Copy quote link"}
        </button>
        <div className="mt-2"><BuyForTeam testid="calc-buy-team-btn" /></div>
      </div>
    </div>
    </>
  );
};

const FalKey = () => (
  <section id="fal-key" className="scroll-mt-6 card p-6 space-y-5" data-testid="ai-falkey-section">
    <div>
      <h2 className="font-display uppercase text-3xl md:text-4xl leading-none">How your own <span style={{ color: MINT }}>fal.ai</span> key works</h2>
      <p className="mt-2 text-sm text-[#9ba3b4] max-w-2xl">The AI models run on fal.ai, billed straight to you at fal's price. You only pay KolorKlinic once, for the plugin.</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {STEPS.map(({ icon: Icon, title, body }) => (
        <div key={title} className="demo-card bg-[#0e1013] border border-[#2a2f3d] p-4" data-testid={`ai-falkey-step-${title[0]}`}>
          <div className="flex items-center gap-2 mb-1"><Icon size={16} style={{ color: MINT }} /><h3 className="font-display uppercase text-base">{title}</h3></div>
          <p className="text-sm text-[#9ba3b4] leading-relaxed">{body}</p>
        </div>
      ))}
    </div>
    <div className="border border-[#2a2f3d] bg-[#0e1013]" data-testid="ai-cost-table">
      {COSTS.map(([what, cost, c]) => (
        <div key={what} className="flex flex-col md:flex-row md:items-center gap-1 md:gap-4 px-4 py-3 border-b border-[#2a2f3d] last:border-b-0">
          <span className="font-display uppercase text-sm md:w-80" style={{ color: c }}>{what}</span>
          <span className="text-sm text-[#f2f4f8] font-mono">{cost}</span>
        </div>
      ))}
    </div>
    <CostCalculator />
    <p className="text-xs text-[#5c6479]">Example costs measured on our test shots; fal.ai sets the prices and they can change. The plugin always shows the real cost.</p>
  </section>
);

const Roadmap = () => (
  <section className="card p-6 flex flex-col md:flex-row gap-5 md:items-center" data-testid="ai-roadmap">
    <Cpu size={28} style={{ color: MINT }} className="flex-none" />
    <div>
      <h2 className="font-display uppercase text-2xl">Next: fully local</h2>
      <p className="text-sm text-[#9ba3b4] max-w-3xl mt-1">We're working on offline versions that run on your own GPU, with no cloud and no per-job cost, as soon as the open models are good enough for finishing work. Licence holders get them as an update.</p>
    </div>
  </section>
);

export default function AiPage() {
  const { bins, dl, rebuild } = useBins(["", "recast-"]);
  const [showSign, setShowSign] = useState(false);
  return (
    <div className="space-y-10" data-testid="ai-page">
      <AiHero />
      <section id="gen-alpha" className="scroll-mt-6 pt-2" data-testid="gen-alpha-header">
        <div className="chip mb-3 text-[#e69d27] border-[#e69d27]">new · €99 one-time · bring your own fal.ai key</div>
        <h2 className="font-display uppercase text-3xl md:text-4xl leading-none">KolorKlinic <span className="text-[#e69d27]">Gen Alpha</span></h2>
        <p className="mt-3 text-sm text-[#9ba3b4] max-w-2xl">AI character matte for long shots. Draw a box or brush on your subject, pull the alpha for the whole clip, and grade or composite with a clean edge. Runs on your own fal.ai account, so there's no subscription.</p>
      </section>

      <GenAlphaGuide />

      <FeatureDemos />


      <section className="card p-6" data-testid="prebuilt-binaries-card">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Package size={18} className="text-[#e69d27]" />
          <h2 className="font-display uppercase text-2xl">Gen Alpha downloads</h2>
          <ManualLink slug="GenAlpha" name="Gen Alpha" color={AMBER} testid="genalpha-manual-download-btn" auto={false} />
          <span className="chip ml-auto" data-testid="downloads-total-chip">
            {dl ? `${dl.total.toLocaleString()} downloads · ${dl.week.toLocaleString()} this week` : "—"}
          </span>
          <span className="chip">unsigned · ad-hoc</span>
          <button
            className="btn-ghost px-3 py-1 text-xs"
            onClick={() => setShowSign((v) => !v)}
            data-testid="toggle-signing-guide-btn"
          >
            {showSign ? "hide" : "show"} signing guide
          </button>
        </div>
        <BinaryGrid prefix="" bins={bins} dl={dl} rebuild={rebuild} />

        {showSign && (
          <div className="mt-5 bg-[#0e1013] border border-[#2a2f3d] p-5 space-y-3" data-testid="signing-guide">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={16} className="text-[#00e5ff]" />
              <h3 className="font-display uppercase text-xl">Signing &amp; notarising the macOS bundle</h3>
            </div>
            <p className="text-sm text-[#9ba3b4]">
              Required if you want the plugin to load on <em>other</em> people's
              Macs without touching Terminal. Needs a paid Apple Developer
              account ($99/yr) and an app-specific password.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-[#f2f4f8]">
              <li>
                Install <code className="font-mono text-[#e69d27]">Developer ID Application: Your Name (TEAMID)</code> in Keychain.
              </li>
              <li>Sign the inner <code className="font-mono">.ofx</code>:
                <pre className="font-mono text-xs bg-black/50 p-2 mt-1 border border-[#2a2f3d] whitespace-pre-wrap">codesign --timestamp --options runtime --force \
  --sign "Developer ID Application: Your Name (TEAMID)" \
  KolorKlinicGenAlpha.ofx.bundle/Contents/MacOS/KolorKlinicGenAlpha.ofx</pre>
              </li>
              <li>Sign the bundle:
                <pre className="font-mono text-xs bg-black/50 p-2 mt-1 border border-[#2a2f3d] whitespace-pre-wrap">codesign --timestamp --options runtime --force --deep \
  --sign "Developer ID Application: Your Name (TEAMID)" \
  KolorKlinicGenAlpha.ofx.bundle</pre>
              </li>
              <li>Notarise:
                <pre className="font-mono text-xs bg-black/50 p-2 mt-1 border border-[#2a2f3d] whitespace-pre-wrap">ditto -c -k --keepParent KolorKlinicGenAlpha.ofx.bundle plugin.zip
xcrun notarytool submit plugin.zip \
  --apple-id you@example.com --team-id TEAMID \
  --password "app-specific-password" --wait</pre>
              </li>
              <li>Staple + verify:
                <pre className="font-mono text-xs bg-black/50 p-2 mt-1 border border-[#2a2f3d] whitespace-pre-wrap">xcrun stapler staple KolorKlinicGenAlpha.ofx.bundle
spctl -a -t open --context context:primary-signature KolorKlinicGenAlpha.ofx.bundle</pre>
              </li>
            </ol>
          </div>
        )}
      </section>

      <RecastSection bins={bins} dl={dl} rebuild={rebuild} />

      <FalKey />

      <AiPricing />

      <Roadmap />
      <section className="card p-6" data-testid="install-paths-card">
        <div className="flex items-center gap-3 mb-4">
          <Terminal size={18} className="text-[#e69d27]" />
          <h2 className="font-display uppercase text-2xl">Install path</h2>
          <span className="chip ml-auto">drop the unzipped bundles here</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(OS_PATHS).map(([k, v]) => (
            <div key={k} className="bg-[#0e1013] border border-[#2a2f3d] p-4" data-testid={`install-${k}`}>
              <div className="chip mb-2">{v.label}</div>
              <code className="font-mono text-xs text-[#e69d27] break-all leading-6 block">
                {v.path}
              </code>
              <div className="mt-2">
                <CopyBtn value={v.path} testid={`install-copy-${k}`} />
              </div>
              {v.quar && (
                <div className="mt-3 bg-[#1a140b] border border-[#e69d27] p-2 font-mono text-[10px] text-[#f2f4f8] leading-5 break-all">
                  <div className="text-[#e69d27] mb-1 uppercase tracking-widest">first run:</div>
                  {v.quar}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* BACKEND CONFIG */}
      <section className="card p-6" data-testid="backend-card">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="font-display uppercase text-2xl">Backend URL</h2>
          <span className="chip ml-auto">paste into plugin</span>
        </div>
        <p className="text-sm text-[#9ba3b4] mb-4 max-w-2xl">
          After installing, open the plugin in Resolve and paste this URL
          into its <span className="font-mono text-[#e69d27]">Backend URL</span>{" "}
          parameter of Gen Alpha and Recast. Your frames go through here to your own fal.ai account.
        </p>
        <div className="bg-[#0e1013] border border-[#2a2f3d] p-4 flex items-start gap-3">
          <code
            className="font-mono text-sm text-[#e69d27] break-all leading-relaxed flex-1"
            data-testid="backend-url-value"
          >
            {BACKEND_URL}
          </code>
          <CopyBtn value={BACKEND_URL} testid="copy-backend-url-btn" />
        </div>
      </section>

      <SupportSection />
    </div>
  );
}
